import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInAnonymously as firebaseSignInAnonymously,
  signOut as firebaseSignOut,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  EmailAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithCredential,
  linkWithPopup,
  signInWithCredential,
  deleteUser,
} from '@firebase/auth';
import type { AuthCredential } from '@firebase/auth';
import type { FirebaseError } from '@firebase/app';
import { doc, setDoc, getDoc, updateDoc, deleteDoc, serverTimestamp } from '@firebase/firestore';
import { auth, db } from './firebase';
import { COLLECTIONS } from './collections';
import { User } from '../types';
import { mapDoc } from '@utils/firestore';
import { AppError, isFirebaseAuthError } from '@utils/errorHandler';
import { createPrefixedLogger } from '@utils/logger';
import { UserSchema } from '../schemas/firestoreSchemas';
import type { SignUpData, SignInData } from '../types/api';
import type { UserRole } from '../types';

const log = createPrefixedLogger('auth');

export type { SignUpData, SignInData } from '../types/api';

/**
 * Split a Google account's `displayName` into first/last for the user doc
 * (Firestore stores these separately, matching the email sign-up form).
 * Only the first whitespace-delimited token becomes `firstName`; everything
 * after is joined back together as `lastName` (so "Ada Mae Lovelace" ->
 * firstName "Ada", lastName "Mae Lovelace"). Null/empty input yields both
 * fields empty rather than throwing — a missing Google display name
 * shouldn't block sign-in.
 */
export function splitDisplayName(displayName: string | null): {
  firstName: string;
  lastName: string;
} {
  const trimmed = displayName?.trim() ?? '';
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }
  const [firstName, ...rest] = trimmed.split(/\s+/);
  return { firstName, lastName: rest.join(' ') };
}

// `select_account` forces Google's account chooser even when the browser has
// a single Google session — without it a returning user with one Google
// account can get silently re-signed-in without a chance to pick a
// different one.
function googleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

// Set by upgradeFromAnonymousWithGoogle when linkWithPopup fails because the
// Google identity already owns a separate account; signInWithPendingGoogleCredential
// consumes it to sign into that existing account without re-running the popup.
let pendingGoogleCredential: AuthCredential | null = null;

/** Every account is at least a Fire Scout; de-dupe and preserve order. */
function withFireScout(roles?: UserRole[]): UserRole[] {
  return Array.from(new Set<UserRole>(['fire-scout', ...(roles ?? [])]));
}

/**
 * The same, minus the two roles a client is not allowed to self-assert.
 * firestore.rules refuses them, and an agent needs provisionAgentProfile,
 * which only the email sign-up path calls.
 */
function normalizeSignUpRoles(roles?: UserRole[]): UserRole[] {
  return withFireScout(roles).filter((r) => r !== 'insurance-agent' && r !== 'insurance-company');
}

export const authApi = {
  /** True when the signed-in session carries the `moderator` custom claim
   *  (set via scripts/agent-ops.mjs grant-moderator; mirrored by
   *  firestore.rules isModerator()). False for signed-out sessions and on
   *  any token-read failure — moderation is a convenience surface, never
   *  worth blocking auth flow on. */
  async getModeratorClaim(): Promise<boolean> {
    try {
      const result = await auth.currentUser?.getIdTokenResult();
      return result?.claims.moderator === true;
    } catch {
      return false;
    }
  },

  async signUp({
    email,
    password,
    firstName,
    lastName,
    roles,
    insurerName,
    inviteCode,
  }: SignUpData): Promise<User> {
    const normalizedRoles: UserRole[] = withFireScout(roles);
    const isAgent = normalizedRoles.includes('insurance-agent');

    // Validate invite code before creating the user (runs unauthenticated).
    let inviteResult: { agentId: string; insurerName: string } | null = null;
    if (!isAgent && inviteCode?.trim()) {
      const { validateInviteCode } = await import('./invites');
      inviteResult = await validateInviteCode(inviteCode);
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;

    const userData: Record<string, unknown> = {
      email,
      firstName,
      lastName,
      roles: normalizedRoles,
      emailNotificationsEnabled: true,
      postsCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    if (inviteResult) {
      userData.invitedByAgentId = inviteResult.agentId;
      userData.defaultInsurer = inviteResult.insurerName;
    }

    const userRef = doc(db, COLLECTIONS.USERS, userId);
    await setDoc(userRef, userData);

    // Self-service agent signup: the agentProfile is written server-side by
    // the provisionAgentProfile callable (Firestore rules forbid the client
    // from writing agentProfile, and the invite code is generated there with
    // server-side crypto). It is created in a 'pending' state and unlocks no
    // insurance privilege until an admin runs verifyAgent — this closes the
    // "anyone can self-assert they are an agent" hole (audit F2).
    if (isAgent && insurerName?.trim()) {
      const { provisionAgentProfile } = await import('./agents');
      await provisionAgentProfile(insurerName);
    }

    // Re-read the just-created doc so the returned User has real Timestamps
    const created = mapDoc(await getDoc(userRef), UserSchema);
    if (!created) {
      throw new AppError('Failed to read newly created user profile.', 'FIRESTORE_ERROR');
    }
    return created;
  },

  async signIn({ email, password }: SignInData): Promise<void> {
    await signInWithEmailAndPassword(auth, email, password);
  },

  /**
   * Sign in (or first-time sign up) via the Google popup flow. Web only —
   * see GoogleSignInButton for the native no-op.
   *
   * Returns `null` when the user dismisses the popup instead of throwing —
   * closing the account chooser is a routine cancel, not an error, so
   * callers shouldn't surface a banner for it.
   */
  /**
   * `extraRoles` carries the audience from an intro page (/start/<audience>)
   * into a brand new Google account. Passed down from the screen through
   * authStore rather than read from uiStore here, because api/ never reaches
   * up into store/ (the layering rule in CLAUDE.md). Ignored for an existing
   * account: roles there are the user's to edit, not a link's to overwrite.
   */
  async signInWithGoogle(extraRoles?: UserRole[]): Promise<User | null> {
    let result;
    try {
      result = await signInWithPopup(auth, googleProvider());
    } catch (error) {
      if (
        isFirebaseAuthError(error, 'auth/popup-closed-by-user') ||
        isFirebaseAuthError(error, 'auth/cancelled-popup-request')
      ) {
        return null;
      }
      throw error;
    }

    const userId = result.user.uid;
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const existing = await getDoc(userRef);
    if (!existing.exists()) {
      const { firstName, lastName } = splitDisplayName(result.user.displayName);
      // Deliberately no hasCompletedOnboarding (new Google users get the
      // same welcome experience as a fresh signUp) and no invite-code
      // redemption / agent provisionAgentProfile call — those only run
      // through the email sign-up form, which asks for the invite code /
      // insurer name Google's popup has no field for.
      //
      // insurance-agent and insurance-company are dropped here even if asked
      // for: rules refuse them from a client, and an agent needs
      // provisionAgentProfile, which only the email path calls.
      const roles = normalizeSignUpRoles(extraRoles);
      await setDoc(userRef, {
        email: result.user.email ?? '',
        firstName,
        lastName,
        roles,
        emailNotificationsEnabled: true,
        postsCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    const created = mapDoc(await getDoc(userRef), UserSchema);
    if (!created) {
      throw new AppError('Failed to read user profile.', 'FIRESTORE_ERROR');
    }
    return created;
  },

  /**
   * Sign the user in as a Firebase Anonymous Auth account and seed a
   * matching Fire Scout user doc. Used for the "no signup" guest path —
   * the user gets a real uid (so posts/comments still attribute) but no
   * email/password until they later "upgrade" via linkWithCredential.
   */
  async signInAsScout(): Promise<User> {
    const cred = await firebaseSignInAnonymously(auth);
    const userId = cred.user.uid;

    const userRef = doc(db, COLLECTIONS.USERS, userId);
    const existing = await getDoc(userRef);
    if (!existing.exists()) {
      await setDoc(userRef, {
        email: '',
        firstName: 'Fire',
        lastName: 'Scout',
        roles: ['fire-scout'],
        isAnonymous: true,
        emailNotificationsEnabled: false,
        postsCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    const created = mapDoc(await getDoc(userRef), UserSchema);
    if (!created) {
      throw new AppError('Failed to read anonymous user profile.', 'FIRESTORE_ERROR');
    }
    return created;
  },

  /**
   * Convert the current anonymous Fire Scout into a durable email/password
   * account by linking new credentials to the existing uid. Posts and
   * comments stay attributed to the same uid; the user doc updates with
   * the new email + name and flips `isAnonymous` to false.
   *
   * Throws if the caller isn't currently signed in as an anonymous user
   * or if Firebase rejects the link (most commonly: email already in use
   * by another account).
   */
  async upgradeFromAnonymous(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<User> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new AppError('Not signed in.', 'AUTH_ERROR');
    }
    if (!currentUser.isAnonymous) {
      throw new AppError('Account is already a full account; nothing to upgrade.', 'AUTH_ERROR');
    }

    const credential = EmailAuthProvider.credential(data.email, data.password);
    // linkWithCredential preserves the uid — the difference vs.
    // createUserWithEmailAndPassword is critical: a fresh-create would
    // orphan every post/comment the scout authored under the old uid.
    await linkWithCredential(currentUser, credential);

    const userRef = doc(db, COLLECTIONS.USERS, currentUser.uid);
    await updateDoc(userRef, {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      isAnonymous: false,
      emailNotificationsEnabled: true,
      // Anonymous Fire Scouts skipped onboarding entirely (see _layout's
      // isAnonScout branch). Marking onboarding complete on upgrade keeps
      // them on the screen they were already using — without this the
      // route guard yanks newly-upgraded users into /onboarding/welcome
      // mid-flow (e.g., right after upgrading inside the Add Property sheet).
      hasCompletedOnboarding: true,
      updatedAt: serverTimestamp(),
    });

    const updated = mapDoc(await getDoc(userRef), UserSchema);
    if (!updated) {
      throw new AppError('Failed to read upgraded user profile.', 'FIRESTORE_ERROR');
    }
    return updated;
  },

  /**
   * Convert the current anonymous Fire Scout into a durable account via the
   * Google popup flow — the linkWithPopup counterpart to
   * upgradeFromAnonymous. `linkWithPopup` preserves the uid the same way
   * `linkWithCredential` does above, so posts/comments/claims made while
   * anonymous keep their attribution.
   *
   * Returns a status instead of throwing for the two non-fatal outcomes:
   * - 'cancelled': the user dismissed the popup.
   * - 'account-exists': that Google identity already owns a separate
   *   (non-anonymous) account. The credential is stashed on
   *   `pendingGoogleCredential` so `signInWithPendingGoogleCredential` can
   *   sign into the existing account without re-running the popup — the
   *   guest's own posts stay on the guest profile, unmerged.
   */
  async upgradeFromAnonymousWithGoogle(): Promise<
    { status: 'upgraded'; user: User } | { status: 'cancelled' } | { status: 'account-exists' }
  > {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new AppError('Not signed in.', 'AUTH_ERROR');
    }
    if (!currentUser.isAnonymous) {
      throw new AppError('Account is already a full account; nothing to upgrade.', 'AUTH_ERROR');
    }

    let result;
    try {
      result = await linkWithPopup(currentUser, googleProvider());
    } catch (error) {
      if (
        isFirebaseAuthError(error, 'auth/popup-closed-by-user') ||
        isFirebaseAuthError(error, 'auth/cancelled-popup-request')
      ) {
        return { status: 'cancelled' };
      }
      if (isFirebaseAuthError(error, 'auth/credential-already-in-use')) {
        pendingGoogleCredential = GoogleAuthProvider.credentialFromError(error as FirebaseError);
        return { status: 'account-exists' };
      }
      throw error;
    }

    const userRef = doc(db, COLLECTIONS.USERS, currentUser.uid);
    const { firstName, lastName } = splitDisplayName(result.user.displayName);
    await updateDoc(userRef, {
      email: result.user.email ?? '',
      firstName,
      lastName,
      isAnonymous: false,
      emailNotificationsEnabled: true,
      // Same route-guard reasoning as upgradeFromAnonymous above: mark
      // onboarding complete so the guard doesn't yank a newly-upgraded user
      // into /onboarding/welcome mid-flow.
      hasCompletedOnboarding: true,
      updatedAt: serverTimestamp(),
    });

    const updated = mapDoc(await getDoc(userRef), UserSchema);
    if (!updated) {
      throw new AppError('Failed to read upgraded user profile.', 'FIRESTORE_ERROR');
    }
    return { status: 'upgraded', user: updated };
  },

  /**
   * Consume the credential stashed by upgradeFromAnonymousWithGoogle's
   * 'account-exists' outcome and sign into that existing account. The auth
   * state listener hydrates the store's user/onboarding state once this
   * resolves — same as every other sign-in path.
   */
  async signInWithPendingGoogleCredential(): Promise<void> {
    const cred = pendingGoogleCredential;
    pendingGoogleCredential = null;
    if (!cred) {
      throw new AppError('No pending Google sign-in.', 'AUTH_ERROR');
    }
    await signInWithCredential(auth, cred);
  },

  async signOut(): Promise<void> {
    await firebaseSignOut(auth);
  },

  async sendPasswordResetEmail(email: string): Promise<void> {
    await firebaseSendPasswordResetEmail(auth, email);
  },

  async getCurrentUser(): Promise<User | null> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return null;
    }

    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, currentUser.uid));
    return mapDoc(userDoc, UserSchema);
  },

  // Best-effort by design: both callers treat a roles save as non-fatal
  // (an account upgrade or sheet close shouldn't fail because of it), so
  // errors are logged rather than thrown.
  async updateUserRoles(userId: string, fields: Pick<User, 'roles'>): Promise<void> {
    try {
      await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
        ...fields,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      log.error('failed to update roles:', error);
    }
  },

  async updateProfile(
    userId: string,
    data: Partial<
      Pick<
        User,
        'firstName' | 'lastName' | 'emailNotificationsEnabled' | 'avatarUrl' | 'avatarPath'
      >
    >
  ): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  },

  async deleteAccount(userId: string): Promise<void> {
    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.uid !== userId) {
      throw new AppError('Unauthorized', 'PERMISSION_DENIED');
    }

    await deleteDoc(doc(db, COLLECTIONS.USERS, userId));
    await deleteUser(currentUser);
  },

  /**
   * Subscribe to auth state. Emits `{ user }` on the happy path. The failure
   * modes below used to all collapse into a silent signed-out UI while Firebase
   * kept a live session (sign-in modal closes, but no avatar ever appears):
   *
   * - `getDoc` throws (offline / transient rules hiccup): previously became a
   *   swallowed unhandled rejection so the callback never fired and the app sat
   *   on its loading splash. Now emits `{ user: null }` with the session left
   *   intact so a reconnect/reload recovers.
   * - Doc exists but fails `UserSchema` (corruption / schema drift): logged
   *   loudly and surfaced via `error` instead of a mute signed-out screen. The
   *   session is deliberately NOT signed out — a schema-tightening deploy must
   *   not mass-evict existing sessions.
   * - Doc absent: expected briefly while `signUp`/`signInAsScout` create the
   *   auth user before writing its doc (those flows set `user` directly), so it
   *   stays silently signed-out here and the create flow owns the result.
   */
  onAuthStateChanged(
    callback: (result: { user: User | null; error?: string }) => void
  ): () => void {
    return firebaseOnAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        callback({ user: null });
        return;
      }
      let userDoc;
      try {
        userDoc = await getDoc(doc(db, COLLECTIONS.USERS, firebaseUser.uid));
      } catch (error) {
        log.error('Failed to load user doc for active session', firebaseUser.uid, error);
        callback({ user: null });
        return;
      }
      const user = mapDoc(userDoc, UserSchema);
      if (user) {
        callback({ user });
        return;
      }
      if (userDoc.exists()) {
        log.error('Active session user doc failed schema validation', firebaseUser.uid);
        callback({
          user: null,
          error: 'We had trouble loading your account. Please reload and sign in again.',
        });
        return;
      }
      callback({ user: null });
    });
  },
};
